import { ref, type Ref } from "vue";
import type FormCC from "@/interface/FormCC";
import type FormCCOtp from "@/interface/FormCCOtp";
import type PopulatedIdInfo from "@/interface/PopulatedIdInfo";
import type MultiChannelLink from "@/interface/MultiChannelLink";

//Resume Auto Population
export default function setResumeAutoPopulation(
  ccFormValue: Ref<FormCC>,
  prop: any,
  otp: Ref<FormCCOtp>,
  creditLimit: Ref<string>,
  biometricVerification: Ref<string>,
  populatedIdInfo: Ref<PopulatedIdInfo>,
  multiChannel: Ref<MultiChannelLink>,
  emit: (
    event:
      | "nextStep"
      | "previousStep"
      | "jumpStep"
      | "update:currentStep"
      | "submit"
      | "ekycGenerate"
      | "listenerEkyc"
      | "setCardId",
    ...args: any[]
  ) => void, // Strict type
  filteredMobile: (number: string) => string, // Accept the function as a parameter
): void {
  if ((prop.isDraft || prop.isAutoPopulate) && prop.reminderValue) {
    otp.value.verified = "true";
    if (prop.resumeWebFormRequestId)
      ccFormValue.value.webFormRequestId = prop.resumeWebFormRequestId;
    if (prop.reminderValue.dropOffStep)
      ccFormValue.value.dropOffStep = prop.reminderValue.dropOffStep;
    if (prop.reminderValue.otpStatus)
      if (prop.reminderValue.otpStatus == "Y")
        otp.value.otpMobile.validated = true;
      else otp.value.otpMobile.validated = false;
    // resume only skip ekyc when ekyc null or fail
    if (
      (prop.reminderValue.ekycStatus == "SKIP" ||
        (!prop.reminderValue.ekycStatus && prop.type == "FINPLUS") ||
        prop.reminderValue.ekycStatus == "FAILED") &&
      prop.isDraft
    ) {
      ccFormValue.value.ekyc.status = "SKIP";
    } else if (
      prop.reminderValue.ekycStatus &&
      prop.reminderValue.ekycStatus == "COMPLETE"
    ) {
      ccFormValue.value.ekyc.status = prop.reminderValue.ekycStatus;
    }
    if (prop.reminderValue.emailVerification)
      if (prop.reminderValue.emailVerification == "Y")
        otp.value.otpEmail.validated = true;
      else otp.value.otpEmail.validated = false;
    if (prop.reminderValue.ctosScore)
      ccFormValue.value.ctosScore = prop.reminderValue.ctosScore;
    if (prop.reminderValue.preAssessStatus)
      ccFormValue.value.ekyc.preStatus = prop.reminderValue.preAssessStatus;
    if (prop.reminderValue.preAssessmentId)
      ccFormValue.value.preAss.webFormRequestId =
        prop.reminderValue.preAssessmentId;
    if (prop.reminderValue.dsrStatus)
      ccFormValue.value.dsrBurst = prop.reminderValue.dsrStatus;
    if (prop.reminderValue.creditLimit)
      creditLimit.value = prop.reminderValue.creditLimit;
    if (prop.reminderValue.refNo)
      ccFormValue.value.refNo = prop.reminderValue.refNo;
    if (prop.reminderValue.cardId) emit("setCardId", prop.reminderValue.cardId);
    if (prop.reminderValue.suppWebFormRequestId) {
      for (let i = 0; i < prop.reminderValue.suppWebFormRequestId.length; i++) {
        ccFormValue.value.supplementary.webFormRequestId =
          prop.reminderValue.suppWebFormRequestId[i];
      }
    }
    if (prop.reminderValue.suppOtpStatus) {
      for (let i = 0; i < prop.reminderValue.suppOtpStatus.length; i++) {
        otp.value.supplementaryOtp.success =
          prop.reminderValue.suppOtpStatus[i].toString();
      }
    }
    if (prop.reminderValue.biometricVerification)
      biometricVerification.value = "true";
    else biometricVerification.value = "false";
    if (prop.reminderValue.webFormDetails.documentUploadUrl) {
      ccFormValue.value.imageFilePath =
        prop.reminderValue.webFormDetails.documentUploadUrl;
    }
    ccFormValue.value.ekyc.completedInCC =
      !prop.reminderValue.mosEkycVerification;

    //basic information
    if (prop.reminderValue.webFormDetails.oriBasicInformation) {
      if (prop.reminderValue.webFormDetails.oriBasicInformation.fullName)
        ccFormValue.value.basicInformation.name =
          prop.reminderValue.webFormDetails.oriBasicInformation.fullName.toUpperCase();
      if (prop.reminderValue.webFormDetails.oriBasicInformation.idTypeCode)
        ccFormValue.value.basicInformation.idType =
          prop.reminderValue.webFormDetails.oriBasicInformation.idTypeCode;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.idNo)
        ccFormValue.value.basicInformation.idNo =
          prop.reminderValue.webFormDetails.oriBasicInformation.idNo;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.oldIc)
        ccFormValue.value.basicInformation.oldIc =
          prop.reminderValue.webFormDetails.oriBasicInformation.oldIc;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.militaryNo)
        ccFormValue.value.basicInformation.militaryNo =
          prop.reminderValue.webFormDetails.oriBasicInformation.militaryNo;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.nationalityCode)
        ccFormValue.value.basicInformation.nationality =
          prop.reminderValue.webFormDetails.oriBasicInformation.nationalityCode;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.dateOfBirth)
        ccFormValue.value.basicInformation.dob =
          prop.reminderValue.webFormDetails.oriBasicInformation.dateOfBirth;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.emailAddress)
        ccFormValue.value.basicInformation.email =
          prop.reminderValue.webFormDetails.oriBasicInformation.emailAddress;
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation.grossMonthlySalary
      )
        ccFormValue.value.basicInformation.grossSalary = Number(
          prop.reminderValue.webFormDetails.oriBasicInformation
            .grossMonthlySalary,
        );
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation.netMonthlySalary
      )
        ccFormValue.value.basicInformation.netSalary = Number(
          prop.reminderValue.webFormDetails.oriBasicInformation
            .netMonthlySalary,
        );
      if (prop.reminderValue.webFormDetails.oriBasicInformation.professionCode)
        ccFormValue.value.basicInformation.profession =
          prop.reminderValue.webFormDetails.oriBasicInformation.professionCode;
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation.professionOthers
      )
        ccFormValue.value.basicInformation.others =
          prop.reminderValue.webFormDetails.oriBasicInformation.professionOthers;
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation.mobileCountryCode
      )
        ccFormValue.value.basicInformation.countryCode = filteredMobile(
          prop.reminderValue.webFormDetails.oriBasicInformation
            .mobileCountryCode,
        );
      if (prop.reminderValue.webFormDetails.oriBasicInformation.mobileNumber)
        ccFormValue.value.basicInformation.mobileNo =
          prop.reminderValue.webFormDetails.oriBasicInformation.mobileNumber;
      if (!prop.isAutoPopulate || prop.type == "CR_SL") {
        if (prop.reminderValue.webFormDetails.oriBasicInformation.anyLoan)
          ccFormValue.value.basicInformation.question1 = "Yes";
        else ccFormValue.value.basicInformation.question1 = "No";
      }
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation
          .homePhoneCountryCode
      )
        ccFormValue.value.basicInformation.homeCountryCode = filteredMobile(
          prop.reminderValue.webFormDetails.oriBasicInformation
            .homePhoneCountryCode,
        );
      if (prop.reminderValue.webFormDetails.oriBasicInformation.homePhoneNumber)
        ccFormValue.value.basicInformation.homePhoneNo =
          prop.reminderValue.webFormDetails.oriBasicInformation.homePhoneNumber;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.repayment)
        ccFormValue.value.basicInformation.repayment = Number(
          prop.reminderValue.webFormDetails.oriBasicInformation.repayment,
        );
      if (!prop.isAutoPopulate) {
        ccFormValue.value.basicInformation.consent = "true";
      }
      if (
        prop.reminderValue.webFormDetails.oriBasicInformation.jcklMembershipNo
      )
        ccFormValue.value.basicInformation.jcklMembershipNo =
          prop.reminderValue.webFormDetails.oriBasicInformation.jcklMembershipNo;
      if (prop.reminderValue.webFormDetails.oriBasicInformation.jcklClubStatus)
        ccFormValue.value.basicInformation.jcklClubStatus =
          prop.reminderValue.webFormDetails.oriBasicInformation.jcklClubStatus;
    }

    // personal information
    if (prop.reminderValue.webFormDetails.oriPersonInformation) {
      if (prop.reminderValue.webFormDetails.oriPersonInformation.title)
        ccFormValue.value.personalInformation.title =
          prop.reminderValue.webFormDetails.oriPersonInformation.title;
      if (prop.reminderValue.webFormDetails.oriPersonInformation.genderCode)
        ccFormValue.value.personalInformation.gender =
          prop.reminderValue.webFormDetails.oriPersonInformation.genderCode;
      if (prop.reminderValue.webFormDetails.oriPersonInformation.raceCode)
        ccFormValue.value.personalInformation.race =
          prop.reminderValue.webFormDetails.oriPersonInformation.raceCode;
      if (prop.reminderValue.webFormDetails.oriPersonInformation.otherRace)
        ccFormValue.value.personalInformation.otherRace =
          prop.reminderValue.webFormDetails.oriPersonInformation.otherRace;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .residencyStatusCode
      )
        ccFormValue.value.personalInformation.residencyStatus =
          prop.reminderValue.webFormDetails.oriPersonInformation.residencyStatusCode;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation.maritalStatusCode
      )
        ccFormValue.value.personalInformation.maritalStatus =
          prop.reminderValue.webFormDetails.oriPersonInformation.maritalStatusCode;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation.motherMaidenName
      )
        ccFormValue.value.personalInformation.motherMaidenName =
          prop.reminderValue.webFormDetails.oriPersonInformation.motherMaidenName;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .numberOfDependents != null
      )
        ccFormValue.value.personalInformation.dependency =
          prop.reminderValue.webFormDetails.oriPersonInformation.numberOfDependents.toString();
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .residentialOwnership
      )
        ccFormValue.value.personalInformation.residentialOwnership =
          prop.reminderValue.webFormDetails.oriPersonInformation.residentialOwnership;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .academicQualification
      )
        ccFormValue.value.personalInformation.academic =
          prop.reminderValue.webFormDetails.oriPersonInformation.academicQualification;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .otherQualification
      )
        ccFormValue.value.personalInformation.otherAcademic =
          prop.reminderValue.webFormDetails.oriPersonInformation.otherQualification;
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress
      ) {
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine1
        ) {
          ccFormValue.value.personalInformation.permanentAddress1 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.addressLine1.toUpperCase();
          ccFormValue.value.personalInformation.question1 = "No";
        } else ccFormValue.value.personalInformation.question1 = "Yes";
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine2
        )
          ccFormValue.value.personalInformation.permanentAddress2 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.addressLine2.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine3
        )
          ccFormValue.value.personalInformation.permanentAddress3 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.addressLine3.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.city
        )
          ccFormValue.value.personalInformation.city2 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.city;
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.postcode
        )
          ccFormValue.value.personalInformation.postCode2 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.postcode;
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .permanentAddress.state
        )
          ccFormValue.value.personalInformation.state2 =
            prop.reminderValue.webFormDetails.oriPersonInformation.permanentAddress.state;
      } else ccFormValue.value.personalInformation.question1 = "Yes";
      if (
        prop.reminderValue.webFormDetails.oriPersonInformation
          .residentialAddress
      ) {
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine1
        )
          ccFormValue.value.personalInformation.residentialAddress1 =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.addressLine1.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine2
        )
          ccFormValue.value.personalInformation.residentialAddress2 =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.addressLine2.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine3
        )
          ccFormValue.value.personalInformation.residentialAddress3 =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.addressLine3.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.city
        )
          ccFormValue.value.personalInformation.city =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.city;
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.postcode
        )
          ccFormValue.value.personalInformation.postCode =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.postcode;
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.state
        )
          ccFormValue.value.personalInformation.state =
            prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.state;
        if (
          prop.reminderValue.webFormDetails.oriPersonInformation
            .residentialAddress.durationOfStay
        ) {
          if (
            prop.reminderValue.webFormDetails.oriPersonInformation
              .residentialAddress.durationOfStay.month != null
          )
            ccFormValue.value.personalInformation.monthStay =
              prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.durationOfStay.month.toString();
          if (
            prop.reminderValue.webFormDetails.oriPersonInformation
              .residentialAddress.durationOfStay.year != null
          )
            ccFormValue.value.personalInformation.yearStay =
              prop.reminderValue.webFormDetails.oriPersonInformation.residentialAddress.durationOfStay.year.toString();
        }
      }
    }

    // emergency contact
    if (prop.reminderValue.webFormDetails.oriEmergencyContactInfo) {
      if (prop.reminderValue.webFormDetails.oriEmergencyContactInfo.title)
        ccFormValue.value.emergencyContact.title =
          prop.reminderValue.webFormDetails.oriEmergencyContactInfo.title;
      if (prop.reminderValue.webFormDetails.oriEmergencyContactInfo.contactName)
        ccFormValue.value.emergencyContact.relativeName =
          prop.reminderValue.webFormDetails.oriEmergencyContactInfo.contactName;
      if (
        prop.reminderValue.webFormDetails.oriEmergencyContactInfo.relationship
      )
        ccFormValue.value.emergencyContact.relationship =
          prop.reminderValue.webFormDetails.oriEmergencyContactInfo.relationship;
      if (
        prop.reminderValue.webFormDetails.oriEmergencyContactInfo
          .contactNoCountryCode
      )
        ccFormValue.value.emergencyContact.countryCode = filteredMobile(
          prop.reminderValue.webFormDetails.oriEmergencyContactInfo
            .contactNoCountryCode,
        );
      if (prop.reminderValue.webFormDetails.oriEmergencyContactInfo.contactNo)
        ccFormValue.value.emergencyContact.mobileNo =
          prop.reminderValue.webFormDetails.oriEmergencyContactInfo.contactNo;
    }

    // employment detail
    if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo) {
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.my2ndHome !=
        null
      ) {
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.my2ndHome ==
          true
        )
          ccFormValue.value.employmentDetails.question1 = "Yes";
        else ccFormValue.value.employmentDetails.question1 = "No";
      }
      if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.workInMY)
        ccFormValue.value.employmentDetails.question2 =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.workInMY ==
          "true"
            ? "Yes"
            : "No";
      if (
        prop.isDraft &&
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.occupationType
      )
        ccFormValue.value.employmentDetails.occupation =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.occupationType;
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.employmentType
      ) {
        ccFormValue.value.employmentDetails.employment =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.employmentType;
        if (
          ccFormValue.value.employmentDetails.employment == "112" ||
          ccFormValue.value.employmentDetails.employment == "113" ||
          ccFormValue.value.employmentDetails.employment == "114"
        ) {
          ccFormValue.value.employmentDetails.occupation = "0";
        } else if (
          ccFormValue.value.employmentDetails.employment == "111" ||
          ccFormValue.value.employmentDetails.employment == "120" ||
          ccFormValue.value.employmentDetails.employment == "200"
        ) {
          ccFormValue.value.employmentDetails.occupation = "1";
          ccFormValue.value.employmentDetails.question2 = "Yes";
        }
      }
      if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.incomeType)
        ccFormValue.value.employmentDetails.employmentIncome =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.incomeType;
      if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyName)
        ccFormValue.value.employmentDetails.companyName =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyName;
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
          .sstRegistrationNo
      ) {
        ccFormValue.value.employmentDetails.sstRegistrationNo =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.sstRegistrationNo;
        if (prop.isAutoPopulate) {
          populatedIdInfo.value.employmentDetails.sstRegistrationNo =
            ccFormValue.value.employmentDetails.sstRegistrationNo;
        }
      }
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyPhoneNo
      )
        ccFormValue.value.employmentDetails.phoneNo =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyPhoneNo;
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
          .companyPhoneNoCountryCode
      )
        ccFormValue.value.employmentDetails.countryCode = filteredMobile(
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyPhoneNoCountryCode,
        );
      if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.areaCode)
        ccFormValue.value.employmentDetails.areaCode =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.areaCode;
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
          .natureOfBusiness
      )
        ccFormValue.value.employmentDetails.natureBusiness =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.natureOfBusiness;
      if (prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.otherNOB)
        ccFormValue.value.employmentDetails.others =
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.otherNOB;
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress
      ) {
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine1
        )
          ccFormValue.value.employmentDetails.companyAddress1 =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine1.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine2
        )
          ccFormValue.value.employmentDetails.companyAddress2 =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine2.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine3
        )
          ccFormValue.value.employmentDetails.companyAddress3 =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine3.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.city
        )
          ccFormValue.value.employmentDetails.city =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.city;
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.postcode
        ) {
          ccFormValue.value.employmentDetails.postCode =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.postcode;
          if (prop.isAutoPopulate) {
            populatedIdInfo.value.employmentDetails.postcode =
              ccFormValue.value.employmentDetails.postCode;
          }
        }
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.state
        )
          ccFormValue.value.employmentDetails.state =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.companyAddress.state;
      }
      if (
        prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
          .lengthOfEmployment
      ) {
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .lengthOfEmployment.month
        )
          ccFormValue.value.employmentDetails.monthEmp =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.lengthOfEmployment.month.toString();
        if (
          prop.reminderValue.webFormDetails.oriEmploymentDetailInfo
            .lengthOfEmployment.year
        )
          ccFormValue.value.employmentDetails.yearEmp =
            prop.reminderValue.webFormDetails.oriEmploymentDetailInfo.lengthOfEmployment.year;
      }
    }

    // principal card detail
    if (prop.reminderValue.webFormDetails.oriPrincipalCardDetail) {
      if (prop.reminderValue.webFormDetails.oriPrincipalCardDetail.name)
        ccFormValue.value.principalDetails.name =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.name;
      if (prop.reminderValue.webFormDetails.oriPrincipalCardDetail.branchName)
        ccFormValue.value.principalDetails.branch =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.branchName;
      if (prop.reminderValue.webFormDetails.oriPrincipalCardDetail.branchValue)
        ccFormValue.value.principalDetails.branchValue =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.branchValue;
      if (
        prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryOption
      )
        ccFormValue.value.principalDetails.deliveryOption =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryOption;
      if (prop.reminderValue.webFormDetails.oriPrincipalCardDetail.eStatement)
        ccFormValue.value.principalDetails.eStatement = prop.reminderValue
          .webFormDetails.oriPrincipalCardDetail.eStatement
          ? "Yes"
          : "No";
      if (
        prop.reminderValue.webFormDetails.oriPrincipalCardDetail.billingAddress
      )
        ccFormValue.value.principalDetails.billingAddress =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.billingAddress;
      if (
        prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress
      ) {
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.addressLine1
        )
          ccFormValue.value.principalDetails.address1 =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.addressLine1.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.addressLine2
        )
          ccFormValue.value.principalDetails.address2 =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.addressLine2.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.addressLine3
        )
          ccFormValue.value.principalDetails.address3 =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.addressLine3.toUpperCase();
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.city
        )
          ccFormValue.value.principalDetails.city =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.city;
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.postcode
        )
          ccFormValue.value.principalDetails.postCode =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.postcode.toString();
        if (
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail
            .deliveryAddress.state
        )
          ccFormValue.value.principalDetails.state =
            prop.reminderValue.webFormDetails.oriPrincipalCardDetail.deliveryAddress.state;
      }
      if (
        prop.reminderValue.webFormDetails.oriPrincipalCardDetail
          .jcklClubBillPeriod
      )
        ccFormValue.value.principalDetails.jcklClubBillPeriod =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.jcklClubBillPeriod;
      if (
        prop.reminderValue.webFormDetails.oriPrincipalCardDetail.jcklFeeAmount
      )
        ccFormValue.value.principalDetails.jcklFeeAmount =
          prop.reminderValue.webFormDetails.oriPrincipalCardDetail.jcklFeeAmount;
    }

    const newFormData = new FormData();
    newFormData.append("", "");
    // product bundling timeout application populate document
    if (
      prop.reminderValue.pfExpired &&
      prop.reminderValue.productBundlingExpiredtype == "PF_CC"
    ) {
      ccFormValue.value.uploadDocument.proofIdentity.file.push(newFormData);
      ccFormValue.value.uploadDocument.proofIdentity.fileName.push(
        "Personal Financing Documents",
      );
      ccFormValue.value.uploadDocument.proofIdentity.fileNumber += 1;
      ccFormValue.value.uploadDocument.proofIdentity.resumeFileNumber += 1;
      ccFormValue.value.uploadDocument.proofIncome.file.push(newFormData);
      ccFormValue.value.uploadDocument.proofIncome.fileName.push(
        "Personal Financing Documents",
      );
      ccFormValue.value.uploadDocument.proofIncome.fileNumber += 1;
      ccFormValue.value.uploadDocument.proofIncome.resumeFileNumber += 1;
    } else if (
      prop.reminderValue.pfExpired &&
      prop.reminderValue.productBundlingExpiredtype == "EP_CC" &&
      prop.reminderValue.epccDocumentNameDTO
    ) {
      if (
        prop.reminderValue.epccDocumentNameDTO.proofOfIdentityFileName != null
      ) {
        if (
          prop.reminderValue.epccDocumentNameDTO.proofOfIdentityFileName
            .length > 0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.epccDocumentNameDTO.proofOfIdentityFileName
              .length;
            i++
          ) {
            ccFormValue.value.EPCCUploadDocument.proofIdentity.file.push(
              newFormData,
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.fileName.push(
              prop.reminderValue.epccDocumentNameDTO.proofOfIdentityFileName[i],
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.invalidFile.push(
              false,
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.fileNumber += 1;
            ccFormValue.value.EPCCUploadDocument.proofIdentity.resumeFileNumber += 1;
          }
        }
      }

      if (
        prop.reminderValue.epccDocumentNameDTO.proofOfIncomeFileName != null
      ) {
        if (
          prop.reminderValue.epccDocumentNameDTO.proofOfIncomeFileName.length >
          0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.epccDocumentNameDTO.proofOfIncomeFileName.length;
            i++
          ) {
            ccFormValue.value.EPCCUploadDocument.proofIncome.file.push(
              newFormData,
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.fileName.push(
              prop.reminderValue.epccDocumentNameDTO.proofOfIncomeFileName[i],
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.invalidFile.push(
              false,
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.fileNumber += 1;
            ccFormValue.value.EPCCUploadDocument.proofIncome.resumeFileNumber += 1;
          }
        }
      }
    }

    // MOS file
    if (prop.type == "CR_SL" && prop.reminderValue.mosDocumentNameDTO) {
      if (
        prop.reminderValue.mosDocumentNameDTO.proofOfIdentityFileName != null
      ) {
        if (
          prop.reminderValue.mosDocumentNameDTO.proofOfIdentityFileName.length >
          0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.mosDocumentNameDTO.proofOfIdentityFileName
              .length;
            i++
          ) {
            ccFormValue.value.MosUploadDocument.proofIdentity.file.push(
              newFormData,
            );
            ccFormValue.value.MosUploadDocument.proofIdentity.fileName.push(
              prop.reminderValue.mosDocumentNameDTO.proofOfIdentityFileName[i],
            );
            ccFormValue.value.MosUploadDocument.proofIdentity.invalidFile.push(
              false,
            );
            ccFormValue.value.MosUploadDocument.proofIdentity.fileNumber += 1;
            ccFormValue.value.MosUploadDocument.proofIdentity.resumeFileNumber += 1;
          }
        }
      }

      if (prop.reminderValue.mosDocumentNameDTO.proofOfIncomeFileName != null) {
        if (
          prop.reminderValue.mosDocumentNameDTO.proofOfIncomeFileName.length > 0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.mosDocumentNameDTO.proofOfIncomeFileName.length;
            i++
          ) {
            ccFormValue.value.MosUploadDocument.proofIncome.file.push(
              newFormData,
            );
            ccFormValue.value.MosUploadDocument.proofIncome.fileName.push(
              prop.reminderValue.mosDocumentNameDTO.proofOfIncomeFileName[i],
            );
            ccFormValue.value.MosUploadDocument.proofIncome.invalidFile.push(
              false,
            );
            ccFormValue.value.MosUploadDocument.proofIncome.fileNumber += 1;
            ccFormValue.value.MosUploadDocument.proofIncome.resumeFileNumber += 1;
          }
        }
      }
    }

    // upload documents
    if (prop.reminderValue.documentNameDTO) {
      if (prop.reminderValue.documentNameDTO.proofOfIdentityFileName != null) {
        if (
          prop.reminderValue.documentNameDTO.proofOfIdentityFileName.length > 0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.documentNameDTO.proofOfIdentityFileName.length;
            i++
          ) {
            ccFormValue.value.uploadDocument.proofIdentity.file.push(
              newFormData,
            );
            ccFormValue.value.uploadDocument.proofIdentity.fileName.push(
              prop.reminderValue.documentNameDTO.proofOfIdentityFileName[i],
            );
            ccFormValue.value.uploadDocument.proofIdentity.fileNumber += 1;
            ccFormValue.value.uploadDocument.proofIdentity.resumeFileNumber += 1;
          }
        }
      }
      if (prop.reminderValue.documentNameDTO.proofOfIncomeFileName != null) {
        if (
          prop.reminderValue.documentNameDTO.proofOfIncomeFileName.length > 0
        ) {
          for (
            let i = 0;
            i < prop.reminderValue.documentNameDTO.proofOfIncomeFileName.length;
            i++
          ) {
            ccFormValue.value.uploadDocument.proofIncome.file.push(newFormData);
            ccFormValue.value.uploadDocument.proofIncome.fileName.push(
              prop.reminderValue.documentNameDTO.proofOfIncomeFileName[i],
            );
            ccFormValue.value.uploadDocument.proofIncome.fileNumber += 1;
            ccFormValue.value.uploadDocument.proofIncome.resumeFileNumber += 1;
          }
        }
      }
      if (prop.reminderValue.documentNameDTO.proofOfJCKLFileName != null) {
        if (prop.reminderValue.documentNameDTO.proofOfJCKLFileName.length > 0) {
          for (
            let i = 0;
            i < prop.reminderValue.documentNameDTO.proofOfJCKLFileName.length;
            i++
          ) {
            ccFormValue.value.uploadDocument.proofJCKL.file.push(newFormData);
            ccFormValue.value.uploadDocument.proofJCKL.fileName.push(
              prop.reminderValue.documentNameDTO.proofOfJCKLFileName[i],
            );
            ccFormValue.value.uploadDocument.proofJCKL.fileNumber += 1;
            ccFormValue.value.uploadDocument.proofJCKL.resumeFileNumber += 1;
          }
        }
      }
      if (prop.reminderValue.documentNameDTO.supProofOfIdentities) {
        if (
          prop.reminderValue.documentNameDTO.supProofOfIdentities
            .proofOfIdentityFileName.length > 0
        ) {
          for (
            let i = 0;
            i <
            prop.reminderValue.documentNameDTO.supProofOfIdentities
              .proofOfIdentityFileName.length;
            i++
          ) {
            ccFormValue.value.supplementaryUploadDocument.proofIdentity.file.push(
              newFormData,
            );
            ccFormValue.value.supplementaryUploadDocument.proofIdentity.fileName.push(
              prop.reminderValue.documentNameDTO.supProofOfIdentities
                .proofOfIdentityFileName[i],
            );
            ccFormValue.value.supplementaryUploadDocument.proofIdentity.fileNumber += 1;
            ccFormValue.value.supplementaryUploadDocument.proofIdentity.resumeFileNumber += 1;
          }
        }
      }
    }

    // supplementary card details
    if (
      prop.reminderValue.webFormDetails.supplementaryInformation &&
      prop.reminderValue.webFormDetails.supplementaryInformation[0] != null
    ) {
      ccFormValue.value.supplementary.question1 = "Yes";
      for (
        let i = 0;
        i < prop.reminderValue.webFormDetails.supplementaryInformation.length;
        i++
      ) {
        if (prop.reminderValue.webFormDetails.supplementaryInformation[i].title)
          ccFormValue.value.supplementary.title =
            prop.reminderValue.webFormDetails.supplementaryInformation[i].title;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i].fullName
        )
          ccFormValue.value.supplementary.fullName =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].fullName;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .nameOnCard
        )
          ccFormValue.value.supplementary.nameOnCard =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].nameOnCard;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .relationship
        )
          ccFormValue.value.supplementary.relationship =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].relationship;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .nationalityCode
        )
          ccFormValue.value.supplementary.nationality =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].nationalityCode;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .dateOfBirth
        )
          ccFormValue.value.supplementary.dob =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].dateOfBirth;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .idTypeCode
        )
          ccFormValue.value.supplementary.idType =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].idTypeCode;
        if (prop.reminderValue.webFormDetails.supplementaryInformation[i].idNo)
          ccFormValue.value.supplementary.idNo =
            prop.reminderValue.webFormDetails.supplementaryInformation[i].idNo;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .genderCode
        )
          ccFormValue.value.supplementary.gender =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].genderCode;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i].raceCode
        )
          ccFormValue.value.supplementary.race =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].raceCode;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .otherRace
        )
          ccFormValue.value.supplementary.otherRace =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].otherRace;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .motherMaidenName
        )
          ccFormValue.value.supplementary.motherMaidenName =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].motherMaidenName;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .emailAddress
        )
          ccFormValue.value.supplementary.email =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].emailAddress;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i].mobileNo
        )
          ccFormValue.value.supplementary.mobileNo =
            prop.reminderValue.webFormDetails.supplementaryInformation[
              i
            ].mobileNo;
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .mobileNoCountryCode
        )
          ccFormValue.value.supplementary.mobileCountryCode = filteredMobile(
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .mobileNoCountryCode,
          );
        if (
          prop.reminderValue.webFormDetails.supplementaryInformation[i]
            .residentialAddress
        ) {
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.addressLine1
          )
            ccFormValue.value.supplementary.residentialAddress1 =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.addressLine1.toUpperCase();
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.addressLine2
          )
            ccFormValue.value.supplementary.residentialAddress2 =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.addressLine2.toUpperCase();
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.addressLine3
          )
            ccFormValue.value.supplementary.residentialAddress3 =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.addressLine3.toUpperCase();
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.city
          )
            ccFormValue.value.supplementary.city =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.city;
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.postcode
          )
            ccFormValue.value.supplementary.postCode =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.postcode;
          if (
            prop.reminderValue.webFormDetails.supplementaryInformation[i]
              .residentialAddress.state
          )
            ccFormValue.value.supplementary.state =
              prop.reminderValue.webFormDetails.supplementaryInformation[
                i
              ].residentialAddress.state;
        }
      }
    }

    //terms
    if (prop.reminderValue.webFormDetails.terms) {
      if (prop.reminderValue.webFormDetails.terms.taxIdentificationNo) {
        ccFormValue.value.declaration.taxpayer =
          prop.reminderValue.webFormDetails.terms.taxIdentificationNo;
        populatedIdInfo.value.terms.taxIdentificationNo =
          ccFormValue.value.declaration.taxpayer;
      }
    }

    if (prop.reminderValue.merchantCode)
      multiChannel.value.mc = prop.reminderValue.merchantCode;
    if (prop.reminderValue.applicationChannelCode)
      multiChannel.value.acc = prop.reminderValue.applicationChannelCode;
  }
}
